// frontend/src/components/Dashboard/SkillsTab.js
import React, { useState, useEffect, useMemo } from 'react';
import { skillsAPI } from '../../services/api';

function SkillsTab({ character, skills, setSkills, availableXP }) {
  const [skillTiers, setSkillTiers] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSkillTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeParseJson = (val, fallback) => {
    try {
      if (val === null || val === undefined) return fallback;
      if (typeof val === 'string') return val ? JSON.parse(val) : fallback;
      if (typeof val === 'object') return val || fallback;
      return fallback;
    } catch {
      return fallback;
    }
  };

  // Baseline skills (last-saved) used to prevent "refund" changes in UI
  const baselineSkills = useMemo(() => safeParseJson(character?.feats, {}), [character]);

  const normalizeSkill = (skill) => {
    // Supports legacy shape:
    //   { tier, specialization: "Pistols" }
    // and new shape:
    //   { tier, specialization: { name: "Pistols", tier: 2 } }
    const tier = Number(skill?.tier ?? 0);

    const spec = skill?.specialization;
    if (!spec) return { ...skill, tier };

    if (typeof spec === 'string') {
      const name = spec.trim();
      if (!name) return { ...skill, tier, specialization: undefined };
      return {
        ...skill,
        tier,
        specialization: { name, tier: 0 }, // legacy spec name, default spec tier 0
      };
    }

    if (typeof spec === 'object') {
      /**
       * IMPORTANT BUGFIX:
       * Allow a specialization object with an empty name so the UI can render
       * an input after the user clicks "Add specialization".
       *
       * Previously this code did:
       *   if (!name) return { ...skill, tier, specialization: undefined };
       * which immediately removed the specialization you just added (name: '').
       */
      const rawName = (spec?.name ?? '').toString(); // keep as typed (may be empty)
      const specTier = Number(spec?.tier ?? 0);

      return {
        ...skill,
        tier,
        specialization: { name: rawName, tier: Math.max(0, Math.min(specTier, tier)) }, // clamp to base
      };
    }

    return { ...skill, tier, specialization: undefined };
  };

  const normalizeBaselineSkill = (skill) => normalizeSkill(skill || { tier: 0 });

  const loadSkillTiers = async () => {
    try {
      setIsLoading(true);
      const res = await skillsAPI.getTiers();
      setSkillTiers(res.data.tiers || []);
    } catch (error) {
      console.error('Failed to load skill tiers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSkillCost = (currentTier, targetTier) => {
    if (targetTier <= currentTier) return 0;
    let cost = 0;
    for (let i = currentTier + 1; i <= targetTier; i++) {
      if (i === 5) cost += (i * i) * 50; // Legendary
      else cost += (i * i) * 20;
    }
    return cost;
  };

  const getTierInfo = (tier) => {
    return skillTiers.find((t) => t.tier === tier) || { name: 'Untrained', die: 'd4', cost: 0 };
  };

  const clampSpecToBase = (skill) => {
    const s = normalizeSkill(skill);
    if (!s.specialization) return s;
    const base = Number(s.tier ?? 0);
    const specTier = Number(s.specialization.tier ?? 0);
    if (specTier <= base) return s;
    return { ...s, specialization: { ...s.specialization, tier: base } };
  };

  const addNewSkill = () => {
    if (!newSkillName.trim()) return;

    const skillName = newSkillName.trim();
    if (skills[skillName]) {
      alert('Skill already exists!');
      return;
    }

    const newSkill = {
      tier: 0,
      name: skillName,
    };

    setSkills((prev) => ({
      ...prev,
      [skillName]: newSkill,
    }));

    setNewSkillName('');
    setShowAddSkill(false);
  };

  // --- Base Skill Tier Controls (with baseline lock + XP gating) ---
  const upgradeSkill = (skillName) => {
    const current = normalizeSkill(skills[skillName]);
    const currentTier = Number(current.tier ?? 0);
    if (currentTier >= 5) return;

    const targetTier = currentTier + 1;
    const cost = calculateSkillCost(currentTier, targetTier);

    if (cost > availableXP) {
      alert(`Not enough XP! Need ${cost} XP, you have ${availableXP} XP available.`);
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const cur = normalizeSkill(next[skillName]);
      next[skillName] = clampSpecToBase({ ...cur, tier: targetTier });
      return next;
    });
  };

  const downgradeSkill = (skillName) => {
    const current = normalizeSkill(skills[skillName]);
    const currentTier = Number(current.tier ?? 0);

    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    const baselineTier = Number(baseline.tier ?? 0);

    if (currentTier <= 0) {
      // removing skill
      if (baselineTier > 0) {
        alert("You can't remove a skill that has been saved.");
        return;
      }
      setSkills((prev) => {
        const next = { ...prev };
        delete next[skillName];
        return next;
      });
      return;
    }

    const targetTier = currentTier - 1;
    if (targetTier < baselineTier) {
      alert("You can't reduce a skill below its saved level.");
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const cur = normalizeSkill(next[skillName]);
      next[skillName] = clampSpecToBase({ ...cur, tier: targetTier });
      return next;
    });
  };

  const removeSkill = (skillName) => {
    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    const baselineTier = Number(baseline.tier ?? 0);
    if (baselineTier > 0) {
      alert("You can't remove a skill that has been saved.");
      return;
    }
    setSkills((prev) => {
      const next = { ...prev };
      delete next[skillName];
      return next;
    });
  };

  // --- Specialization Controls ---
  const addSpecialization = (skillName) => {
    setSkills((prev) => {
      const next = { ...prev };
      const cur = normalizeSkill(next[skillName]);

      // Check if already has specialization
      if (cur.specialization) {
        return next; // No change if already exists
      }

      const baseTier = Number(cur.tier ?? 0);

      next[skillName] = {
        ...cur,
        specialization: { name: '', tier: baseTier > 0 ? 1 : 0 },
      };
      return next;
    });
  };

  const updateSpecializationName = (skillName, newName) => {
    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    const baselineSpecName = baseline?.specialization?.name ? baseline.specialization.name.trim() : '';
    const baselineHasSpec = !!baselineSpecName;

    if (!newName.trim() && baselineHasSpec) {
      alert("You can't remove a specialization that has been saved.");
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const cur = normalizeSkill(next[skillName]);

      if (!newName.trim()) {
        // Remove specialization
        const { specialization, ...rest } = cur;
        next[skillName] = rest;
      } else {
        next[skillName] = {
          ...cur,
          specialization: {
            ...cur.specialization,
            name: newName,
          },
        };
      }

      return next;
    });
  };

  const upgradeSpecialization = (skillName) => {
    const cur = normalizeSkill(skills[skillName]);
    const baseTier = Number(cur.tier ?? 0);
    const spec = cur.specialization;

    if (!spec?.name) return;
    const specTier = Number(spec.tier ?? 0);

    if (specTier >= baseTier) return; // cannot exceed base
    const targetTier = specTier + 1;

    const cost = calculateSkillCost(specTier, targetTier);
    if (cost > availableXP) {
      alert(`Not enough XP! Need ${cost} XP, you have ${availableXP} XP available.`);
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const s = normalizeSkill(next[skillName]);
      if (!s.specialization?.name) return next;

      next[skillName] = clampSpecToBase({
        ...s,
        specialization: { ...s.specialization, tier: Number(s.specialization.tier ?? 0) + 1 },
      });
      return next;
    });
  };

  const downgradeSpecialization = (skillName) => {
    const cur = normalizeSkill(skills[skillName]);
    const spec = cur.specialization;
    if (!spec?.name) return;

    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    const baselineSpecTier = Number(baseline?.specialization?.tier ?? 0);

    const specTier = Number(spec.tier ?? 0);
    const targetTier = Math.max(0, specTier - 1);

    if (targetTier < baselineSpecTier) {
      alert("You can't reduce a specialization below its saved level.");
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const s = normalizeSkill(next[skillName]);
      if (!s.specialization?.name) return next;

      next[skillName] = {
        ...s,
        specialization: { ...s.specialization, tier: targetTier },
      };
      return next;
    });
  };

  const removeSpecialization = (skillName) => {
    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    const baselineHasSpec = !!baseline?.specialization?.name;

    if (baselineHasSpec) {
      alert("You can't remove a specialization that has been saved.");
      return;
    }

    setSkills((prev) => {
      const next = { ...prev };
      const cur = normalizeSkill(next[skillName]);
      const { specialization, ...rest } = cur;
      next[skillName] = rest;
      return next;
    });
  };

  // Check if changes can be undone
  const canDowngrade = (skillName) => {
    const current = normalizeSkill(skills[skillName]);
    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    return Number(current.tier ?? 0) > Number(baseline.tier ?? 0);
  };

  const canDowngradeSpec = (skillName) => {
    const current = normalizeSkill(skills[skillName]);
    const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
    return Number(current.specialization?.tier ?? 0) > Number(baseline.specialization?.tier ?? 0);
  };

  if (isLoading) {
    return <div className="skills-editor">Loading skills...</div>;
  }

  const skillEntries = Object.entries(skills);

  return (
    <div className="skills-editor">
      {/* Header with Add Skill button */}
      <div className="skills-header">
        <div className="header-content">
          <h3>Skills</h3>
          <button
            type="button"
            className="btn-add-skill"
            onClick={() => setShowAddSkill(true)}
          >
            + Add Skill
          </button>
        </div>

        <div className="system-description">
          <p>
            Skills progress through tiers: <strong>Untrained (d4)</strong> → <strong>Novice (d6)</strong> →{' '}
            <strong> Trained (d8)</strong> → <strong>Expert (d10)</strong> → <strong>Master (d12)</strong> →{' '}
            <strong> Legendary (d20)</strong>
          </p>
          <p className="xp-info">
            Available XP: <span className="xp-amount">{availableXP}</span>
          </p>
        </div>
      </div>

      {/* Add Skill Modal */}
      {showAddSkill && (
        <div className="add-skill-modal-overlay" onClick={() => setShowAddSkill(false)}>
          <div className="add-skill-modal-content" onClick={(e) => e.stopPropagation()}>
            <h4>Add New Skill</h4>
            <input
              type="text"
              className="skill-name-input"
              value={newSkillName}
              onChange={(e) => setNewSkillName(e.target.value)}
              placeholder="Enter skill name..."
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') addNewSkill();
                if (e.key === 'Escape') setShowAddSkill(false);
              }}
            />
            <div className="modal-buttons">
              <button type="button" onClick={addNewSkill} className="btn-confirm">Add</button>
              <button type="button" onClick={() => setShowAddSkill(false)} className="btn-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Skills Table */}
      {skillEntries.length === 0 ? (
        <div className="empty-state">
          <p>No skills yet. Click "Add Skill" to get started.</p>
        </div>
      ) : (
        <div className="skills-table">
          <div className="table-header">
            <div className="col-name">Skill Name</div>
            <div className="col-tier">Tier</div>
            <div className="col-die">Die</div>
            <div className="col-cost">Cost</div>
            <div className="col-actions">Actions</div>
          </div>

          {skillEntries.map(([skillName, skill]) => {
            const normalized = normalizeSkill(skill);
            const currentTier = Number(normalized.tier ?? 0);
            const tierInfo = getTierInfo(currentTier);
            const nextCost = currentTier < 5 ? calculateSkillCost(currentTier, currentTier + 1) : null;
            const canUpgrade = currentTier < 5 && nextCost && availableXP >= nextCost;
            const canDown = canDowngrade(skillName);

            const spec = normalized.specialization;
            const hasSpec = !!(spec && typeof spec === 'object'); // Check if specialization exists at all
            const specTier = hasSpec ? Number(spec.tier ?? 0) : 0;
            const specNextCost =
              hasSpec && specTier < currentTier ? calculateSkillCost(specTier, specTier + 1) : null;
            const canUpgradeSpec =
              hasSpec && specTier < currentTier && specNextCost && availableXP >= specNextCost;
            const canDownSpec = hasSpec && canDowngradeSpec(skillName);

            return (
              <div key={skillName} className="skill-row-group">
                {/* Main Skill Row */}
                <div className="skill-row">
                  <div className="col-name">
                    <span className="skill-name-text">{skillName}</span>
                  </div>
                  <div className="col-tier">{tierInfo.name}</div>
                  <div className="col-die">{tierInfo.die}</div>
                  <div className="col-cost">
                    {currentTier > 0 ? `${calculateSkillCost(0, currentTier)} XP` : '—'}
                  </div>
                  <div className="col-actions">
                    {canUpgrade && (
                      <button
                        type="button"
                        className="action-btn increase"
                        onClick={() => upgradeSkill(skillName)}
                        title={`Upgrade to ${getTierInfo(currentTier + 1).name} (${nextCost} XP)`}
                      >
                        +
                      </button>
                    )}
                    {canDown && (
                      <button
                        type="button"
                        className="action-btn decrease"
                        onClick={() => downgradeSkill(skillName)}
                        title="Undo last upgrade"
                      >
                        −
                      </button>
                    )}
                    <button
                      type="button"
                      className="action-btn add-spec"
                      onClick={() => {
                        if (hasSpec) {
                          alert('This skill already has a specialization');
                          return;
                        }
                        addSpecialization(skillName);
                      }}
                      title={hasSpec ? 'Already has specialization' : 'Add specialization'}
                      style={{
                        opacity: hasSpec ? 0.4 : 1,
                        cursor: hasSpec ? 'not-allowed' : 'pointer',
                      }}
                    >
                      ⊕
                    </button>
                    <button
                      type="button"
                      className="action-btn remove"
                      onClick={() => removeSkill(skillName)}
                      title="Remove skill"
                    >
                      🗑
                    </button>
                  </div>
                </div>

                {/* Specialization Row */}
                {hasSpec && (
                  <div className="specialization-row">
                    <div className="col-name">
                      {(() => {
                        // Check if this specialization exists in the baseline (saved state)
                        const baseline = normalizeBaselineSkill(baselineSkills?.[skillName]);
                        const baselineHasSpec = baseline?.specialization?.name?.trim();
                        const baselineSpecName = baselineHasSpec ? baseline.specialization.name : '';
                        const currentSpecName = spec.name || '';

                        // If baseline has this spec with the same name, show as text
                        // Otherwise, show as editable input
                        if (baselineHasSpec && currentSpecName.trim() === baselineSpecName) {
                          return <span className="spec-name-text">{currentSpecName}</span>;
                        } else {
                          return (
                            <input
                              type="text"
                              className="spec-name-input"
                              value={currentSpecName}
                              onChange={(e) => updateSpecializationName(skillName, e.target.value)}
                              placeholder="Enter specialization name..."
                              autoFocus={!currentSpecName.trim()}
                            />
                          );
                        }
                      })()}
                    </div>
                    <div className="col-tier">Level {specTier}</div>
                    <div className="col-die">+{specTier} modifier</div>
                    <div className="col-cost">
                      {specTier > 0 ? `${calculateSkillCost(0, specTier)} XP` : '—'}
                    </div>
                    <div className="col-actions">
                      {canUpgradeSpec && (
                        <button
                          type="button"
                          className="action-btn increase"
                          onClick={() => upgradeSpecialization(skillName)}
                          title={`Increase level (${specNextCost} XP)`}
                        >
                          +
                        </button>
                      )}
                      {canDownSpec && (
                        <button
                          type="button"
                          className="action-btn decrease"
                          onClick={() => downgradeSpecialization(skillName)}
                          title="Decrease level"
                        >
                          −
                        </button>
                      )}
                      <button
                        type="button"
                        className="action-btn remove"
                        onClick={() => removeSpecialization(skillName)}
                        title="Remove specialization"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .skills-editor {
          padding: 20px;
          font-family: 'Rajdhani', sans-serif;
        }

        .skills-header {
          margin-bottom: 30px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 15px;
          border-bottom: 2px solid #7b2cbf;
        }

        .header-content h3 {
          font-family: 'Cinzel', serif;
          color: #c9a962;
          font-size: 2rem;
          margin: 0;
          text-shadow: 0 0 10px rgba(201, 169, 98, 0.3);
        }

        .btn-add-skill {
          background: linear-gradient(135deg, #7b2cbf 0%, #5a189a 100%);
          color: #e0d8f0;
          border: 2px solid #9d4edd;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-add-skill:hover {
          background: linear-gradient(135deg, #9d4edd 0%, #7b2cbf 100%);
          box-shadow: 0 0 15px rgba(157, 78, 221, 0.4);
          transform: translateY(-2px);
        }

        .system-description {
          color: #d0c8e0;
          background: rgba(157, 78, 221, 0.1);
          border-left: 3px solid #9d4edd;
          padding: 12px;
          border-radius: 6px;
          line-height: 1.6;
        }

        .xp-info {
          margin-top: 10px;
          font-size: 1.1rem;
        }

        .xp-amount {
          color: #c9a962;
          font-weight: 700;
        }

        .add-skill-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
        }

        .add-skill-modal-content {
          background: rgba(15, 5, 29, 0.95);
          border: 2px solid #7b2cbf;
          border-radius: 12px;
          padding: 25px;
          width: 400px;
          max-width: 90%;
        }

        .add-skill-modal-content h4 {
          color: #c9a962;
          font-family: 'Cinzel', serif;
          margin: 0 0 15px 0;
        }

        .skill-name-input {
          width: 100%;
          padding: 12px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid #5a189a;
          border-radius: 6px;
          color: #e0d8f0;
          font-size: 1rem;
          margin-bottom: 15px;
        }

        .modal-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .btn-confirm {
          background: rgba(76, 201, 240, 0.2);
          border: 2px solid #4cc9f0;
          color: #4cc9f0;
          padding: 10px 18px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 700;
        }

        .btn-cancel {
          background: rgba(247, 37, 133, 0.2);
          border: 2px solid #f72585;
          color: #f72585;
          padding: 10px 18px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 700;
        }

        .skills-table {
          border: 2px solid #5a189a;
          border-radius: 10px;
          overflow: hidden;
          background: rgba(90, 24, 154, 0.18);
        }

        .table-header,
        .skill-row,
        .specialization-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr;
          gap: 10px;
          padding: 12px 14px;
          align-items: center;
        }

        .table-header {
          background: rgba(123, 44, 191, 0.3);
          color: #c9a962;
          font-weight: 800;
          border-bottom: 1px solid rgba(157, 78, 221, 0.5);
        }

        .skill-row {
          color: #e0d8f0;
          border-bottom: 1px solid rgba(157, 78, 221, 0.2);
        }

        .skill-row-group:last-child .skill-row {
          border-bottom: none;
        }

        .skill-name-text {
          font-weight: 700;
          color: #c9a962;
        }

        .specialization-row {
          background: rgba(157, 78, 221, 0.08);
          color: #d0c8e0;
          border-bottom: 1px solid rgba(157, 78, 221, 0.2);
          padding-left: 26px;
        }

        .spec-name-input {
          width: 100%;
          padding: 8px 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(157, 78, 221, 0.5);
          border-radius: 6px;
          color: #e0d8f0;
        }

        .spec-name-text {
          font-style: italic;
          color: #9d4edd;
          font-weight: 700;
        }

        .col-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .action-btn {
          border: 2px solid transparent;
          border-radius: 8px;
          padding: 6px 10px;
          cursor: pointer;
          font-weight: 900;
          transition: all 0.2s;
          background: rgba(255, 255, 255, 0.06);
          color: #e0d8f0;
        }

        .action-btn.increase {
          border-color: #4cc9f0;
          color: #4cc9f0;
        }

        .action-btn.decrease {
          border-color: #f72585;
          color: #f72585;
        }

        .action-btn.add-spec {
          border-color: #c9a962;
          color: #c9a962;
        }

        .action-btn.remove {
          border-color: rgba(247, 37, 133, 0.8);
          color: #f72585;
        }

        .action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 0 10px rgba(157, 78, 221, 0.25);
        }

        .empty-state {
          padding: 40px 10px;
          text-align: center;
          color: #9d4edd;
        }
      `}</style>
    </div>
  );
}

export default SkillsTab;
